import { useEffect, useState, useCallback, useRef } from "react";
import {
  Plane, CheckCircle, AlertTriangle, Clock, ChevronRight,
  Send, Wifi, WifiOff, Shield, Star, BarChart3, RefreshCw, Download, X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SP_LABELS  = ["","Fully Alert","Lively","Okay","A Little Tired","Moderately Tired","Extremely Tired","Exhausted"];
const SP_SHORT   = ["","1","2","3","4","5","6","7"];
const SP_COLORS  = ["","text-green-400","text-green-400","text-teal-400","text-amber-400","text-orange-400","text-red-400","text-red-500"];
const SP_BG      = ["","bg-green-500","bg-green-400","bg-teal-400","bg-amber-400","bg-orange-400","bg-red-500","bg-red-600"];
const SP_BORDER  = ["","border-green-500","border-green-400","border-teal-400","border-amber-400","border-orange-400","border-red-500","border-red-600"];

const STRESSORS  = [
  "Weather / Turbulence","Complex ATC","High Traffic Density","Technical Issue / MEL",
  "Challenging Approach","TCAS RA","Emergency / Abnormal","Short Rest Before Sector",
  "Night Duty / WOCL","Heavy Cockpit Workload","Passenger Disruption","Other",
];

async function apiGet(path) {
  const token = localStorage.getItem("fs_token");
  const r = await fetch(`${API}/api/sfr${path}`, { headers: { Authorization: `Bearer ${token}` } });
  return r.json();
}
async function apiPost(path, body) {
  const token = localStorage.getItem("fs_token");
  const r = await fetch(`${API}/api/sfr${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return r.json();
}

// ─── Sector Card ──────────────────────────────────────────────────────────────
function SectorCard({ sector, onRate }) {
  const isNight = sector.is_night;
  return (
    <div
      className={`bg-card border rounded-sm p-4 active:scale-98 transition-transform cursor-pointer ${sector.rated ? "border-green-500/30 opacity-70" : "border-border hover:border-primary/40"}`}
      onClick={() => !sector.rated && onRate(sector)}
      data-testid={`sector-card-${sector.sector_id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-sm flex items-center justify-center shrink-0 ${sector.rated ? "bg-green-500/10" : "bg-primary/10"}`}>
            <Plane size={18} className={sector.rated ? "text-green-400" : "text-primary"} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono font-black text-foreground text-base">{sector.flight_number}</span>
              {isNight && <span className="text-[9px] font-bold bg-purple-500/15 text-purple-400 border border-purple-500/20 px-1.5 py-0.5 rounded-sm">NIGHT</span>}
              {sector.rated && <span className="text-[9px] font-bold bg-green-500/15 text-green-400 border border-green-500/20 px-1.5 py-0.5 rounded-sm">RATED</span>}
            </div>
            <div className="text-sm font-bold text-foreground mt-0.5">{sector.route}</div>
            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Clock size={10} /> {sector.std} – {sector.sta}</span>
              <span>{sector.block_hours}h block</span>
              <span className="font-mono text-[10px]">{sector.tail}</span>
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{sector.date}</div>
          </div>
        </div>
        {!sector.rated && (
          <ChevronRight size={18} className="text-muted-foreground shrink-0" />
        )}
      </div>
    </div>
  );
}

// ─── SFR Rating Form ──────────────────────────────────────────────────────────
function SFRForm({ sector, pilotName, onSubmitted, onBack }) {
  const [rating, setRating] = useState(0);
  const [stressors, setStressors] = useState([]);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const toggleStressor = (s) =>
    setStressors(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);
    const res = await apiPost("/submit", {
      sector_id:    sector.sector_id,
      flight_number: sector.flight_number,
      route:        sector.route,
      sector_date:  sector.date,
      sfr_rating:   rating,
      stressors,
      comment,
      pilot_name:   pilotName,
      pilot_role:   "Captain",
    });
    setResult(res);
    setSubmitting(false);
    if (res.success) {
      setTimeout(() => onSubmitted(), 2500);
    }
  };

  if (result?.success) return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4 px-6 text-center">
      <div className={`w-20 h-20 rounded-full flex items-center justify-center ${result.alert_flagged ? "bg-red-500/15 border-2 border-red-500/30" : "bg-green-500/15 border-2 border-green-500/30"}`}>
        {result.alert_flagged ? <AlertTriangle size={36} className="text-red-400" /> : <CheckCircle size={36} className="text-green-400" />}
      </div>
      <div>
        <div className="text-xl font-black text-foreground">{result.sp_label}</div>
        <div className="font-mono text-sm text-muted-foreground mt-1">{result.ref}</div>
      </div>
      <div className={`text-sm font-medium px-4 py-2 rounded-sm ${result.alert_flagged ? "bg-red-500/10 text-red-400 border border-red-500/20" : "bg-green-500/10 text-green-400 border border-green-500/20"}`}>
        {result.message}
      </div>
      {result.fatigue_delta !== 0 && (
        <div className="text-xs text-muted-foreground">
          Fatigue score adjusted: <span className={result.fatigue_delta > 0 ? "text-red-400 font-mono" : "text-green-400 font-mono"}>{result.fatigue_delta > 0 ? `+${result.fatigue_delta}` : result.fatigue_delta} pts</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Sector info */}
      <div className="bg-card border border-border rounded-sm p-4">
        <div className="flex items-center gap-3">
          <Plane size={16} className="text-primary" />
          <div>
            <div className="font-mono font-black text-foreground">{sector.flight_number} · {sector.route}</div>
            <div className="text-xs text-muted-foreground">{sector.date} · {sector.std}–{sector.sta} · {sector.block_hours}h</div>
          </div>
        </div>
      </div>

      {/* Samn-Perelli Scale */}
      <div>
        <div className="text-sm font-black uppercase tracking-widest text-muted-foreground mb-1">How do you feel right now?</div>
        <div className="text-[11px] text-muted-foreground mb-4">Samn-Perelli Crew Status Check (1 = Fully alert · 7 = Exhausted)</div>
        <div className="grid grid-cols-7 gap-1.5">
          {[1,2,3,4,5,6,7].map(n => (
            <button key={n} onClick={() => setRating(n)}
              className={`flex flex-col items-center justify-center h-16 rounded-sm border-2 transition-all ${
                rating === n ? `${SP_BG[n]} border-transparent text-white` : `bg-card ${SP_BORDER[n]} ${SP_COLORS[n]} hover:opacity-80`
              }`}
              data-testid={`sp-rating-${n}`}>
              <span className="text-xl font-black">{n}</span>
            </button>
          ))}
        </div>
        {rating > 0 && (
          <div className={`mt-3 text-center text-sm font-bold ${SP_COLORS[rating]}`}>
            {SP_LABELS[rating]}
          </div>
        )}
        {rating >= 5 && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-sm text-xs text-red-400">
            <AlertTriangle size={11} /> Rating ≥ 5 will be flagged to your safety team.
          </div>
        )}
      </div>

      {/* Stressors */}
      <div>
        <div className="text-sm font-black uppercase tracking-widest text-muted-foreground mb-3">What made this sector demanding? (optional)</div>
        <div className="flex flex-wrap gap-2">
          {STRESSORS.map(s => (
            <button key={s} onClick={() => toggleStressor(s)}
              className={`text-xs px-3 py-2 rounded-sm border transition-colors ${
                stressors.includes(s)
                  ? "bg-primary text-primary-foreground border-transparent"
                  : "bg-card border-border text-muted-foreground hover:border-primary/40"
              }`}
              data-testid={`stressor-${s.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Comment */}
      <div>
        <div className="text-sm font-black uppercase tracking-widest text-muted-foreground mb-2">Additional comment (optional)</div>
        <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3}
          placeholder="Any specific concerns, observations or recommendations..."
          className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none" />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <button onClick={onBack}
          className="flex-1 py-3 border border-border text-sm text-muted-foreground rounded-sm hover:text-foreground transition-colors">
          Back
        </button>
        <button onClick={handleSubmit} disabled={!rating || submitting}
          className="flex-1 flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground text-sm font-bold rounded-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
          data-testid="submit-sfr-btn">
          <Send size={14} /> {submitting ? "Submitting..." : "Submit Rating"}
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function MySectors() {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSector, setSelectedSector] = useState(null);
  const [pilotName, setPilotName] = useState("Capt. Vikram Singh");
  const [online, setOnline] = useState(navigator.onLine);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const pollRef = useRef(null);

  // Detect pilot name from JWT
  useEffect(() => {
    try {
      const token = localStorage.getItem("fs_token");
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        if (payload.name) setPilotName(payload.name);
      }
    } catch (_) {}
    window.addEventListener("online",  () => setOnline(true));
    window.addEventListener("offline", () => setOnline(false));

    // Detect PWA install eligibility
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    setIsStandalone(standalone);
    const ios = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
    setIsIOS(ios);
    if (!standalone) setShowInstallBanner(true);

    // Chrome/Android install prompt
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (installPrompt) {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === "accepted") setShowInstallBanner(false);
    }
  }, [installPrompt]);

  const loadSectors = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet("/recent-sectors");
      setSectors(d.sectors || []);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSectors();
    // Poll every 30s for real-time updates from other submissions
    pollRef.current = setInterval(loadSectors, 30000);
    return () => clearInterval(pollRef.current);
  }, [loadSectors]);

  const handleSubmitted = () => {
    setSelectedSector(null);
    loadSectors();
  };

  const unrated = sectors.filter(s => !s.rated);
  const rated   = sectors.filter(s => s.rated);

  return (
    <div className="min-h-screen bg-background" data-testid="my-sectors-page">
      {/* Mobile Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-primary" />
              <span className="text-sm font-black text-foreground tracking-tight">Fatigue Shield</span>
              <span className={`w-1.5 h-1.5 rounded-full ${online ? "bg-green-400" : "bg-red-400"}`} />
            </div>
            <div className="text-[10px] text-muted-foreground">{pilotName}</div>
          </div>
          <div className="flex items-center gap-2">
            {online ? <Wifi size={13} className="text-green-400" /> : <WifiOff size={13} className="text-red-400" />}
            <button onClick={loadSectors} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors">
              <RefreshCw size={14} />
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 py-5 max-w-lg mx-auto space-y-5">
        {/* PWA install banner */}
        {showInstallBanner && !isStandalone && (
          <div className="flex items-center gap-3 p-3 bg-primary/8 border border-primary/20 rounded-sm" data-testid="install-banner">
            <Download size={16} className="text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold text-foreground">Install Fatigue Shield</div>
              {isIOS ? (
                <div className="text-[10px] text-muted-foreground">Tap the Share icon (□↑) → "Add to Home Screen"</div>
              ) : installPrompt ? (
                <div className="text-[10px] text-muted-foreground">Add to your home screen for quick sector ratings</div>
              ) : (
                <div className="text-[10px] text-muted-foreground">Open in Chrome → tap ⋮ → "Add to Home Screen"</div>
              )}
            </div>
            {installPrompt && !isIOS && (
              <button onClick={handleInstall}
                className="shrink-0 px-2.5 py-1.5 bg-primary text-primary-foreground text-[10px] font-bold rounded-sm"
                data-testid="install-app-btn">
                Install
              </button>
            )}
            <button onClick={() => setShowInstallBanner(false)} className="text-muted-foreground hover:text-foreground transition-colors shrink-0">
              <X size={13} />
            </button>
          </div>
        )}
        {selectedSector ? (
          <>
            <div className="text-lg font-black text-foreground">Rate Your Sector</div>
            <SFRForm sector={selectedSector} pilotName={pilotName} onSubmitted={handleSubmitted} onBack={() => setSelectedSector(null)} />
          </>
        ) : (
          <>
            {/* Summary bar */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: "To Rate",  value: unrated.length, color: "text-primary" },
                { label: "Rated",    value: rated.length,   color: "text-green-400" },
                { label: "Sectors",  value: sectors.length, color: "text-foreground" },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-card border border-border rounded-sm p-3 text-center">
                  <div className={`text-2xl font-mono font-black ${color}`}>{value}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">{label}</div>
                </div>
              ))}
            </div>

            {loading ? (
              <div className="text-center py-10 text-muted-foreground text-sm animate-pulse">Loading your sectors...</div>
            ) : (
              <>
                {unrated.length > 0 && (
                  <div className="space-y-3">
                    <div className="text-xs font-black uppercase tracking-widest text-muted-foreground">Pending Rating</div>
                    {unrated.map(s => (
                      <SectorCard key={s.sector_id} sector={s} onRate={setSelectedSector} />
                    ))}
                  </div>
                )}

                {unrated.length === 0 && (
                  <div className="flex flex-col items-center py-10 text-center space-y-2">
                    <CheckCircle size={32} className="text-green-400" />
                    <div className="text-sm font-bold text-foreground">All sectors rated</div>
                    <div className="text-xs text-muted-foreground">Great job! Your ratings help improve crew safety.</div>
                  </div>
                )}

                {rated.length > 0 && (
                  <div className="space-y-3">
                    <div className="text-xs font-black uppercase tracking-widest text-muted-foreground">Rated</div>
                    {rated.map(s => (
                      <SectorCard key={s.sector_id} sector={s} onRate={() => {}} />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Info footer */}
            <div className="text-[10px] text-muted-foreground text-center border-t border-border pt-4">
              All ratings are confidential. High-stress ratings (≥ 5) are flagged to the safety team.<br />
              Samn-Perelli Crew Status Check · ICAO Doc 9966 aligned
            </div>
          </>
        )}
      </div>
    </div>
  );
}
